import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingBag, X, PencilLine, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import ProductImage from '@/components/ProductImage';
import { useWishlist } from '@/contexts/WishlistContext';
import { useCart, apiErrorMessage } from '@/contexts/CartContext';
import { imagesForColor } from '@/hooks/useVariantSelection';
import { cn, formatPrice } from '@/lib/utils';

// عنصر مفضلة: التركيبة المحفوظة ثابتة (لتغييرها: "تعديل الاختيار" يفتح صفحة المنتج)
const WishlistItem = ({ item }) => {
  const { toggleWishlist } = useWishlist();
  const { addToCart, findCartItem, openCart } = useCart();
  const [adding, setAdding] = useState(false);
  const { product, variant } = item;
  // نفس التركيبة موجودة في السلة بالفعل؟
  const inCart = findCartItem(item.variant_id);
  const productUrl = `/products/${product.id}?variant=${item.variant_id}`;
  const price = variant?.price ?? product.price;
  const originalPrice = product.original_price > price ? product.original_price : null;
  // صورة لون التركيبة المحفوظة
  const valueIds = (product.variants || []).find((v) => v.id === item.variant_id)?.value_ids || [];
  const colorId = (product.colors || []).find((color) => valueIds.includes(color.id))?.id;
  const image = imagesForColor(product.images || [], colorId)[0];

  const handleRemove = async () => {
    try {
      await toggleWishlist(product, item.variant_id, item.options_label);
      toast.success('تمت الإزالة من المفضلة', { description: item.options_label });
    } catch (error) {
      toast.error('تعذر تحديث المفضلة');
    }
  };

  const handleAddToCart = async () => {
    setAdding(true);
    try {
      await addToCart(item.variant_id, 1);
      toast.success(`تمت إضافة ${product.name_ar} إلى السلة`, { description: item.options_label });
    } catch (error) {
      toast.error(apiErrorMessage(error, 'فشلت إضافة المنتج'));
    } finally {
      setAdding(false);
    }
  };

  return (
    <article
      className={cn(
        'group relative flex gap-4 p-3 md:p-4 bg-white rounded-2xl border shadow-sm transition-all duration-300 hover:shadow-lg',
        inCart ? 'border-emerald-300 ring-1 ring-emerald-100' : 'border-gray-200/80 hover:border-burgundy-500/30'
      )}
    >
      <Link to={productUrl} className="relative shrink-0 w-24 sm:w-28 md:w-32 aspect-[3/4] rounded-xl overflow-hidden bg-gray-100">
        <ProductImage
          src={image?.url}
          name={product.name_ar}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          textClassName="text-xs md:text-sm"
        />
        {inCart && (
          <span className="absolute bottom-0 inset-x-0 bg-emerald-600/90 text-white text-[11px] font-cairo font-bold text-center py-1">
            في السلة
          </span>
        )}
      </Link>

      <div className="flex flex-col flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <Link to={productUrl} className="font-cairo font-bold text-brand-black text-base md:text-lg line-clamp-2 hover:text-burgundy-500 transition-colors">
            {product.name_ar}
          </Link>
          <button
            type="button"
            onClick={handleRemove}
            aria-label="إزالة من المفضلة"
            title="إزالة من المفضلة"
            className="shrink-0 -mt-1 -ml-1 w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-destructive hover:bg-destructive/10 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {variant?.options?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {variant.options.map((option) => (
              <span
                key={option.name}
                title={option.name}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-cairo font-semibold"
              >
                {option.hex && (
                  <span className="w-3.5 h-3.5 rounded-full ring-1 ring-black/15" style={{ backgroundColor: option.hex }} />
                )}
                {option.value}
              </span>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3">
          <p className="text-burgundy-500 font-bold text-lg md:text-xl">{formatPrice(price)}</p>
          {originalPrice && <p className="text-gray-400 line-through text-sm">{formatPrice(originalPrice)}</p>}
          {inCart && (
            <span className="inline-flex items-center gap-1 text-xs font-cairo font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full">
              <CheckCircle2 className="w-3.5 h-3.5" />
              في السلة ×{inCart.quantity}
            </span>
          )}
        </div>

        <div className="mt-auto pt-3 flex flex-wrap items-center gap-2">
          {inCart ? (
            <Button
              onClick={openCart}
              variant="outline"
              className="rounded-full px-4 md:px-5 h-9 md:h-10 font-cairo gap-2 border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white"
            >
              <CheckCircle2 className="w-4 h-4" />
              عرض في السلة
            </Button>
          ) : (
            <Button
              onClick={handleAddToCart}
              disabled={adding || !variant}
              className="bg-burgundy-500 hover:bg-burgundy-600 rounded-full px-4 md:px-5 h-9 md:h-10 font-cairo gap-2"
            >
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
              أضف للسلة
            </Button>
          )}
          <Link
            to={productUrl}
            className="inline-flex items-center gap-1.5 h-9 md:h-10 px-3 rounded-full text-sm font-cairo font-semibold text-gray-600 hover:text-burgundy-500 hover:bg-burgundy-500/5 transition-colors"
          >
            <PencilLine className="w-4 h-4" />
            تعديل الاختيار
          </Link>
        </div>
      </div>
    </article>
  );
};

const WishlistSkeleton = () => (
  <div className="flex gap-4 p-3 md:p-4 bg-white rounded-2xl border border-gray-200/80">
    <Skeleton className="shrink-0 w-24 sm:w-28 md:w-32 aspect-[3/4] rounded-xl" />
    <div className="flex-1 space-y-3 py-1">
      <Skeleton className="h-5 w-2/3" />
      <div className="flex gap-2">
        <Skeleton className="h-6 w-16 rounded-full" />
        <Skeleton className="h-6 w-20 rounded-full" />
      </div>
      <Skeleton className="h-6 w-24" />
      <Skeleton className="h-9 w-32 rounded-full" />
    </div>
  </div>
);

const Wishlist = () => {
  const { items, loading } = useWishlist();
  const { addToCart, findCartItem } = useCart();
  const [addingAll, setAddingAll] = useState(false);
  const availableItems = items.filter((item) => item.variant && !findCartItem(item.variant_id));

  const handleAddAll = async () => {
    setAddingAll(true);
    let added = 0;
    for (const item of availableItems) {
      try {
        await addToCart(item.variant_id, 1);
        added += 1;
      } catch {
        // نكمل الباقي، والعدد النهائي بيوضح اللي اتضاف
      }
    }
    setAddingAll(false);
    added > 0
      ? toast.success(`تمت إضافة ${added} منتج إلى السلة`)
      : toast.error('تعذرت إضافة المنتجات إلى السلة');
  };

  return (
    <div className="py-8 md:py-12 min-h-[60vh]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6 md:mb-8 pb-6 border-b border-gray-200/80">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-burgundy-500/10 flex items-center justify-center">
              <Heart className="w-6 h-6 text-burgundy-500 fill-burgundy-500" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-tajawal font-bold text-brand-black">المفضلة</h1>
              {!loading && (
                <p className="font-cairo text-sm text-gray-500 mt-0.5">
                  {items.length > 0 ? `${items.length} منتج محفوظ` : 'لا توجد منتجات بعد'}
                </p>
              )}
            </div>
          </div>

          {availableItems.length > 0 && items.length > 1 && (
            <Button
              onClick={handleAddAll}
              disabled={addingAll}
              variant="outline"
              className="rounded-full border-burgundy-500 text-burgundy-500 hover:bg-burgundy-500 hover:text-white font-cairo gap-2"
            >
              {addingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
              أضف الباقي للسلة ({availableItems.length})
            </Button>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <WishlistSkeleton key={i} />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 px-6 text-center bg-white rounded-3xl border border-dashed border-gray-300 flex flex-col items-center">
            <div className="relative mb-6">
              <div className="w-24 h-24 rounded-full bg-burgundy-500/10 flex items-center justify-center">
                <Heart className="w-11 h-11 text-burgundy-500" strokeWidth={1.5} />
              </div>
              <span className="absolute -bottom-1 -left-1 w-8 h-8 rounded-full bg-brand-gold/20 flex items-center justify-center">
                <Heart className="w-4 h-4 text-brand-gold fill-brand-gold" />
              </span>
            </div>
            <h2 className="font-tajawal font-bold text-xl text-brand-black mb-2">قائمة المفضلة فارغة</h2>
            <p className="font-cairo text-gray-500 mb-6 max-w-sm">
              اضغطي على القلب في أي منتج بعد اختيار اللون والمقاس، ورح ينحفظ هون عشان ترجعيله بسهولة.
            </p>
            <Link to="/products">
              <Button className="bg-burgundy-500 hover:bg-burgundy-600 rounded-full px-8 font-cairo">تصفحي المنتجات</Button>
            </Link>
          </div>
        ) : (
          <div className={cn('grid grid-cols-1 gap-4 md:gap-5', items.length > 1 && 'lg:grid-cols-2')}>
            {items.map((item) => (
              <WishlistItem key={item.variant_id} item={item} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Wishlist;
